import { createSlice } from '@reduxjs/toolkit'
import { normalizeModuleAccess } from '../utils/moduleAccess'

function readPermissionsFromStorage() {
  try {
    const parsed = JSON.parse(localStorage.getItem('permissions') || '[]')
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function readModuleAccessFromStorage() {
  try {
    return normalizeModuleAccess(
      JSON.parse(localStorage.getItem('moduleAccess') || '[]')
    )
  } catch {
    return []
  }
}

function persistUserBlob(state) {
  localStorage.setItem(
    'user',
    JSON.stringify({
      id: state.id,
      name: state.name,
      role: state.role,
      permissions: state.permissions || [],
      moduleAccess: state.moduleAccess || [],
    })
  )
}

const getInitialState = () => {
  const storedUser = localStorage.getItem('user')
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser)
      return {
        id: user.id || localStorage.getItem('id') || null,
        name: user.name || localStorage.getItem('name') || null,
        role: user.role || localStorage.getItem('role') || null,
        permissions: user.permissions || readPermissionsFromStorage(),
        moduleAccess: Array.isArray(user.moduleAccess)
          ? normalizeModuleAccess(user.moduleAccess)
          : readModuleAccessFromStorage(),
      }
    } catch (e) {
      return {
        id: localStorage.getItem('id') || null,
        name: localStorage.getItem('name') || null,
        role: localStorage.getItem('role') || null,
        permissions: readPermissionsFromStorage(),
        moduleAccess: readModuleAccessFromStorage(),
      }
    }
  }
  return {
    id: localStorage.getItem('id') || null,
    name: localStorage.getItem('name') || null,
    role: localStorage.getItem('role') || null,
    permissions: readPermissionsFromStorage(),
    moduleAccess: readModuleAccessFromStorage(),
  }
}

const userSlice = createSlice({
  name: 'user',
  initialState: getInitialState(),
  reducers: {
    setUser: (state, action) => {
      const userData = action.payload || {}
      const permissions = Array.isArray(userData.permissions)
        ? userData.permissions
        : state.permissions || readPermissionsFromStorage()
      const moduleAccess = Array.isArray(userData.moduleAccess)
        ? normalizeModuleAccess(userData.moduleAccess)
        : state.moduleAccess || readModuleAccessFromStorage()

      if (userData.id != null) state.id = userData.id
      if (userData.name != null) state.name = userData.name
      if (userData.role != null) state.role = userData.role
      state.permissions = permissions
      state.moduleAccess = moduleAccess

      if (state.id) localStorage.setItem('id', state.id)
      if (state.name != null) localStorage.setItem('name', state.name)
      if (state.role != null) localStorage.setItem('role', state.role)
      localStorage.setItem('permissions', JSON.stringify(permissions))
      localStorage.setItem('moduleAccess', JSON.stringify(moduleAccess))
      persistUserBlob(state)
    },
    updatePermissions: (state, action) => {
      const { permissions, role, moduleAccess } = action.payload || {}
      if (Array.isArray(permissions)) {
        state.permissions = permissions
        localStorage.setItem('permissions', JSON.stringify(permissions))
      }
      if (role) {
        state.role = role
        localStorage.setItem('role', role)
      }
      if (Array.isArray(moduleAccess)) {
        state.moduleAccess = normalizeModuleAccess(moduleAccess)
        localStorage.setItem(
          'moduleAccess',
          JSON.stringify(state.moduleAccess)
        )
      }
      persistUserBlob(state)
    },
    clearUser: (state) => {
      state.id = null
      state.name = null
      state.role = null
      state.permissions = []
      state.moduleAccess = []

      localStorage.removeItem('id')
      localStorage.removeItem('name')
      localStorage.removeItem('role')
      localStorage.removeItem('permissions')
      localStorage.removeItem('moduleAccess')
      localStorage.removeItem('user')
    },
  },
})

export const { setUser, clearUser, updatePermissions } = userSlice.actions
export default userSlice.reducer
